import React from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { CommonTheme, useCurrentTheme } from 'app/shared/theme';
import { formattedEventDateAndTime } from 'app/shared/utils/eventDateTime';
import {
  AttendeeState,
  City,
  CityForClp,
  EventForCompactEventCard,
} from 'app/shared/typings';
import { NavigationLink } from 'app/shared/components/atoms';
import {
  neighborhoodOrSecretLocation,
  useEventAndAttendeeStatusMessaging,
} from 'app/fan/utils';
import { useGetEventTagsProps } from 'app/fan/utils/useGetEventTagsProps';
import { CompactItemCard, StatusBar } from 'app/fan/components/molecules';

interface CompactEventCardProps {
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  event: EventForCompactEventCard;
  city?: City | CityForClp;
  userAttendeeState?: AttendeeState;
  numTickets?: number;
  onClick?: () => void;
  displayStatusMessage?: boolean;
  w?: string;
  'data-qaid': string;
}

const getTitle = (
  event: EventForCompactEventCard,
  formatMessage: IntlShape['formatMessage'],
  city?: City | CityForClp
) => {
  if (city && city.cachedSlug === event.city.cachedSlug) {
    return neighborhoodOrSecretLocation(event, formatMessage);
  } else {
    return event.city.title;
  }
};

export const CompactEventCard: React.FC<
  React.PropsWithChildren<CompactEventCardProps>
> = ({
  backgroundColor,
  hoverBackgroundColor,
  event,
  city,
  userAttendeeState,
  numTickets,
  onClick,
  displayStatusMessage = false,
  w = '100%',
  'data-qaid': qaId,
}) => {
  const { formatMessage } = useIntl();
  const { borderRadius } = useCurrentTheme() as CommonTheme;
  const getEventTagsProps = useGetEventTagsProps();

  const { statusMessage, messageType } = useEventAndAttendeeStatusMessaging({
    event,
    attendeeState: userAttendeeState,
    numTickets,
  });
  const { eventShortMonth, eventDay, eventShortWeekday } =
    formattedEventDateAndTime(
      event.guestsArriveAt,
      event.city.timezone,
      event.city.country.countryCode
    );

  return (
    <NavigationLink
      textDecoration="none"
      href={`/events/${event.id}`}
      onClick={onClick}
      controlChildSVGStyle={false}
      w={w}
      h="100%"
      data-qaid={qaId}
    >
      <CompactItemCard
        backgroundColor={backgroundColor}
        hoverBackgroundColor={hoverBackgroundColor}
        title={getTitle(event, formatMessage, city)}
        dateMonth={eventShortMonth}
        dateDay={eventDay}
        dateWeekday={eventShortWeekday}
        itemTagsProps={getEventTagsProps(
          event,
          userAttendeeState,
          displayStatusMessage
        )}
      >
        {displayStatusMessage && (
          <StatusBar
            data-qaid="user-dashboard-status-bar"
            statusMessage={statusMessage}
            messageType={messageType}
            fontSize="fontSize75"
            lineHeight="100%"
            borderRadiusBottomLeft={borderRadius.card}
            borderRadiusBottomRight={borderRadius.card}
          />
        )}
      </CompactItemCard>
    </NavigationLink>
  );
};
